package com.islamicmedia.shared.presentation

import com.islamicmedia.shared.data.api.AnthropicApiClient
import com.islamicmedia.shared.domain.model.CancelFlowConfig
import com.islamicmedia.shared.domain.model.CancelReasonOption
import com.islamicmedia.shared.domain.model.RetentionOfferConfig
import com.islamicmedia.shared.platform.Analytics
import com.islamicmedia.shared.platform.Event
import com.islamicmedia.shared.platform.Param
import com.islamicmedia.shared.util.platformName
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Where the user is in the cancellation conversation. */
sealed interface CancelStep {
    /** "What's the main reason you're leaving?" */
    data object Survey : CancelStep

    /** One save attempt. [index]/[total] drive the "2 of 3" progress affordance. */
    data class Offer(val offer: RetentionOfferConfig, val index: Int, val total: Int) : CancelStep

    /** Last chance to back out. Only reached when there are no offers left to make. */
    data object FinalConfirm : CancelStep

    /** They took an offer — nothing was cancelled. */
    data class Saved(val offer: RetentionOfferConfig, val detailIso: String?) : CancelStep

    /** Cancelled. [endsAtIso] is when their access actually stops. */
    data class Farewell(val endsAtIso: String?) : CancelStep
}

data class CancelUiState(
    val step: CancelStep = CancelStep.Survey,
    /**
     * The survey answers to offer, from the admin-editable config. Starts as the built-in default
     * so the first frame is never an empty question, and is replaced if the server has an edited
     * flow. A failed fetch therefore shows the shipped flow rather than nothing.
     */
    val reasons: List<CancelReasonOption> = CancelFlowConfig.DEFAULT.activeReasons,
    val selectedReason: CancelReasonOption? = null,
    val note: String = "",
    val loading: Boolean = false,
    val error: String? = null,
    /** When their access ends if they go through with it. Shown on the offer steps as the stake. */
    val periodEndIso: String? = null,
    /**
     * Non-null while cancelling would cost them the free month a referral gave them (they are still
     * in their first month): the date their next bill will be if they stay. Both platforms
     * show ReferralCopy.CANCEL_WARNING_TITLE / cancelWarning(date) on every step before they
     * confirm, so they know what they are giving up while they can still keep it.
     */
    val referralFreeMonthUntilIso: String? = null,
    /**
     * The server refused to run an in-app flow for this member — they bill somewhere we can't
     * write to. The UI hands them to the billing portal instead of showing a dead end.
     */
    val useBillingPortal: Boolean = false,
)

/**
 * Drives the cancellation flow. Both platforms render this and nothing else — no step logic,
 * no ordering, no API calls of their own.
 *
 * It lives in `shared` for the same reason the copy does (see
 * [com.islamicmedia.shared.domain.model.CancelFlowConfig]): web and mobile cannot share a composable,
 * so anything decided at the call site gets decided twice. A retention funnel that escalates in a
 * different order on iOS than on the web is not one funnel, it's two — and the numbers would never
 * reveal which was which.
 *
 * [scope] comes from the caller (`rememberCoroutineScope()` on both platforms), so the flow dies
 * with the screen that opened it rather than outliving it.
 */
class CancellationController(
    private val api: AnthropicApiClient,
    private val scope: CoroutineScope,
) {
    private val _state = MutableStateFlow(CancelUiState())
    val state: StateFlow<CancelUiState> = _state.asStateFlow()

    private var flowId: String? = null
    private var offers: List<RetentionOfferConfig> = emptyList()
    private var offerIndex = 0

    /**
     * Pull the admin-editable flow. Called as the sheet opens, so the survey is already on screen
     * from the defaults and simply refines if the server has something different. Deliberately not
     * awaited before rendering: a network round trip between tapping Cancel and seeing the first
     * question would be the worst possible place to add latency.
     */
    fun loadConfig() {
        scope.launch {
            api.fetchCancelFlowConfig()?.let { cfg ->
                _state.update { it.copy(reasons = cfg.activeReasons) }
            }
        }
    }

    fun selectReason(reason: CancelReasonOption) {
        _state.update { it.copy(selectedReason = reason, error = null) }
    }

    fun setNote(note: String) {
        _state.update { it.copy(note = note.take(500)) }
    }

    /** Answer the survey and receive the offer sequence. Requires a selected reason. */
    fun submitSurvey() {
        val reason = _state.value.selectedReason ?: return
        if (_state.value.loading) return
        Analytics.logEvent(Event.CANCEL_FLOW_STARTED, mapOf(Param.REASON to reason.key))
        scope.launch {
            _state.update { it.copy(loading = true, error = null) }
            val note = _state.value.note.trim().takeIf { it.isNotBlank() && reason.allowNote }
            val start = api.cancelStart(reason.key, note, platformName)
            if (start == null) {
                // Not an error the user can act on — some members genuinely cannot be cancelled
                // from here (external billing). Send them where it does work.
                _state.update { it.copy(loading = false, useBillingPortal = true) }
                return@launch
            }
            flowId = start.flowId.takeIf { it.isNotBlank() }
            offers = start.offers
            offerIndex = 0
            _state.update {
                it.copy(
                    loading = false,
                    periodEndIso = start.periodEndIso,
                    referralFreeMonthUntilIso = start.referralFreeMonthUntilIso,
                )
            }
            advance()
        }
    }

    /** Take the offer currently on screen. */
    fun acceptOffer() {
        val current = (_state.value.step as? CancelStep.Offer)?.offer ?: return
        if (_state.value.loading) return
        Analytics.logEvent(Event.CANCEL_OFFER_ACCEPTED, mapOf(Param.OFFER to current.key))
        scope.launch {
            _state.update { it.copy(loading = true, error = null) }
            val date = api.cancelOffer(flowId, current.key, accept = true)
            if (date == null) {
                _state.update {
                    it.copy(loading = false, error = "We couldn't apply that just now. Please try again.")
                }
                return@launch
            }
            _state.update {
                it.copy(loading = false, step = CancelStep.Saved(current, date.takeIf { d -> d.isNotBlank() }))
            }
        }
    }

    /** Pass on the offer currently on screen, and move to the next one — or to the exit. */
    fun declineOffer() {
        val current = (_state.value.step as? CancelStep.Offer)?.offer ?: return
        if (_state.value.loading) return
        Analytics.logEvent(Event.CANCEL_OFFER_DECLINED, mapOf(Param.OFFER to current.key))
        scope.launch {
            // Deliberately not awaited for correctness: a decline is bookkeeping, and a user who
            // has said no should not be made to wait on a write that only we care about.
            api.cancelOffer(flowId, current.key, accept = false)
        }
        offerIndex++
        advance()
    }

    /** Go through with the cancellation. */
    fun confirmCancel() {
        if (_state.value.loading) return
        Analytics.logEvent(Event.CANCEL_CONFIRMED, mapOf(Param.REASON to (_state.value.selectedReason?.key ?: "unknown")))
        scope.launch {
            _state.update { it.copy(loading = true, error = null) }
            val endsAt = api.cancelConfirm(flowId)
            if (endsAt == null) {
                _state.update {
                    it.copy(loading = false, error = "We couldn't complete that. Please try again.")
                }
                return@launch
            }
            _state.update {
                it.copy(
                    loading = false,
                    step = CancelStep.Farewell(endsAt.takeIf { e -> e.isNotBlank() } ?: it.periodEndIso)
                )
            }
        }
    }

    fun dismissError() {
        _state.update { it.copy(error = null) }
    }

    /** Show the next offer, or the final confirmation when they've all been made (or there were
     *  none to make, because everything was already used inside the cooldown). */
    private fun advance() {
        val next = offers.getOrNull(offerIndex)
        if (next == null) {
            _state.update { it.copy(step = CancelStep.FinalConfirm) }
        } else {
            _state.update {
                it.copy(step = CancelStep.Offer(next, index = offerIndex + 1, total = offers.size))
            }
        }
    }
}
